import { motion } from 'framer-motion'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

interface PaymentPercentageSelectorProps {
  totalAmount: number
  onPaymentUpdate: (values: { percentage: number; calculatedPaymentValues: { [method: string]: number } }) => void
  paymentMethods: string[]
}

const PaymentPercentageSelector: React.FC<PaymentPercentageSelectorProps> = ({
  totalAmount,
  onPaymentUpdate,
  paymentMethods
}) => {
  const [selectedPercentage, setSelectedPercentage] = useState<number>(50)

  const calculatedValues = (percentage: number) => {
    const calculatedValues: { [method: string]: number } = {}
    const amountToPay = (totalAmount * percentage) / 100

    calculatedValues[paymentMethods[0]] = amountToPay
    calculatedValues[paymentMethods[1]] = totalAmount - amountToPay

    return calculatedValues
  }

  const handlePercentageChange = (percentage: number) => {
    const validPercentage = typeof percentage === 'number' ? percentage : 50
    setSelectedPercentage(validPercentage)
    const calculatedPaymentValues = calculatedValues(validPercentage)
    onPaymentUpdate({ percentage: validPercentage, calculatedPaymentValues })
  }

  useEffect(() => {
    handlePercentageChange(50)
  }, [])

  return (
    <Container>
      <Header>
        <Title>Selecione o percentual de pagamento:</Title>
        <ButtonContainer>
          {[10, 25, 50, 75, 90].map((percentage) => (
            <PercentageButton
              key={percentage}
              onClick={() => handlePercentageChange(percentage)}
              isSelected={percentage === selectedPercentage}
              animate={{
                scale: percentage === selectedPercentage ? 1 : 0.95
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}>
              {percentage}%
            </PercentageButton>
          ))}
        </ButtonContainer>
      </Header>

      <Summary>
        <SummaryTitle>Resumo de valores:</SummaryTitle>
        <SummaryGrid>
          {Object.entries(calculatedValues(selectedPercentage)).map(([method, value], index) => (
            <SummaryCard key={method}>
              <CardLabel>Cartão {index + 1}</CardLabel>
              <CardValue>
                {value.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                })}
              </CardValue>
              <CardPercentage>
                {typeof selectedPercentage === 'number' ? selectedPercentage.toFixed(0) : '0'}%
              </CardPercentage>
            </SummaryCard>
          ))}
        </SummaryGrid>
      </Summary>
    </Container>
  )
}

const Container = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
`

const Header = styled.div`
  padding: 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e5e5;
`

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 20px;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-start;
`

const PercentageButton = styled(motion.button)<{ isSelected: boolean }>`
  padding: 12px 24px;
  background: ${({ isSelected }) => (isSelected ? 'linear-gradient(135deg, #006DE6 0%, #003A7C 100%)' : 'white')};
  color: ${({ isSelected }) => (isSelected ? 'white' : '#006DE6')};
  border: 2px solid #006de6;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 90px;
  height: 48px;

  &:hover {
    background: ${({ isSelected }) => (isSelected ? 'linear-gradient(135deg, #005BC1 0%, #002E63 100%)' : '#E6F0FF')};
  }
`

const Summary = styled.div`
  padding: 24px;
`

const SummaryTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 16px;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`

const SummaryCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CardLabel = styled.span`
  font-size: 14px;
  color: #666;
`

const CardValue = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
`

const CardPercentage = styled.span`
  font-size: 14px;
  color: #006de6;
  font-weight: 500;
`

export default PaymentPercentageSelector
